import React, { useState } from 'react';
import { Reply, Trash2, Send, Loader2 } from 'lucide-react';

const ROLE_STYLES = {
  role: 'bg-rose-50 text-rose-700 border border-rose-100',
  admin: 'bg-violet-100 text-violet-800 border border-violet-200',
  default: 'bg-zinc-100 text-zinc-700 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700',
};

function Badge({ children, variant = 'default' }) {
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md inline-flex items-center gap-1 ${ROLE_STYLES[variant]}`}>
      {children}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'az önce';
  if (s < 3600) return `${Math.floor(s / 60)} dk önce`;
  if (s < 86400) return `${Math.floor(s / 3600)} sa önce`;
  return d.toLocaleDateString('tr-TR');
}

export function ReplyBox({ onSubmit, onCancel }) {
  const [v, setV] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    if (!v.trim()) return;
    setBusy(true);
    try { await onSubmit(v.trim()); setV(''); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-3 flex items-center gap-2">
      <input autoFocus value={v} onChange={e => setV(e.target.value)} placeholder="Yanıtınız..." className="to-input flex-1 !py-2 !text-sm" />
      <button type="button" onClick={onCancel} className="pill-btn !py-2 !px-3 text-xs">İptal</button>
      <button disabled={busy} className="pill-btn !py-2 !px-3 text-xs" style={{ background: 'var(--text)', color: 'var(--bg)', borderColor: 'transparent' }}>
        {busy ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />}
      </button>
    </form>
  );
}

function CommentItem(props) {
  const { c, depth, onReply, onDelete, canDelete, openReply, setOpenReply } = props;
  const isReply = depth > 0;
  return (
    <div className={`flex gap-3 items-start ${isReply ? 'pl-4 sm:pl-8 relative' : ''}`}>
      {isReply && <span className="absolute left-0 top-5 bottom-0 w-px" style={{ background: 'var(--border)' }} />}
      {c.authorAvatar
        ? <img src={c.authorAvatar} alt={c.author} className="w-9 h-9 rounded-full flex-shrink-0" />
        : <div className="w-9 h-9 rounded-full flex-shrink-0" style={{ background: 'var(--pill-hover)' }} />}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold">{c.author}</span>
          {c.isAdmin && <Badge variant="admin">Admin</Badge>}
          {c.role && !c.isAdmin && <Badge variant="role">{c.role}</Badge>}
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{timeAgo(c.createdAt)}</span>
        </div>
        <div className="text-sm mt-1 whitespace-pre-wrap" style={{ color: 'var(--text)' }}>{c.body}</div>
        <div className="mt-2 flex items-center gap-3 text-xs">
          <button onClick={() => setOpenReply(openReply === c.id ? null : c.id)} className="inline-flex items-center gap-1 hover-link" style={{ color: 'var(--text-muted)' }}>
            <Reply size={12} /> Yanıtla
          </button>
          {canDelete(c) && (
            <button onClick={() => onDelete(c.id)} className="inline-flex items-center gap-1 text-rose-500 hover:text-rose-600 transition-colors">
              <Trash2 size={12} /> Sil
            </button>
          )}
        </div>
        {openReply === c.id && (
          <ReplyBox onSubmit={(text) => { onReply(text, c.id); setOpenReply(null); }} onCancel={() => setOpenReply(null)} />
        )}
      </div>
    </div>
  );
}

export default function CommentThread({ top, replies, onReply, onDelete, canDelete, openReply, setOpenReply }) {
  return (
    <div>
      <CommentItem
        c={top}
        depth={0}
        onReply={onReply}
        onDelete={onDelete}
        canDelete={canDelete}
        openReply={openReply}
        setOpenReply={setOpenReply}
      />
      {replies && replies.length > 0 && (
        <div className="mt-3 ml-12 flex flex-col gap-3">
          {replies.map(r => (
            <CommentItem
              key={r.id}
              c={r}
              depth={1}
              onReply={onReply}
              onDelete={onDelete}
              canDelete={canDelete}
              openReply={openReply}
              setOpenReply={setOpenReply}
            />
          ))}
        </div>
      )}
    </div>
  );
}
